/**
 * P6 (AN-PP6 §2.1/§2.4) — política agregada do produto (`ProductPolicy` do PDF §10) e fila de aprovação.
 *
 * Espelha `PoliticaAgregadaResponse` de `originei-produtos` (records `Componentes`, `Componente`, `Comissao`,
 * `ComissaoProduto`, `Tributos`, `Ausente`, `Pendente`). Tudo é RETRATO montado pelo backend (projeção por evento):
 * status, vigência, componentes, ausências. A tela não deriva nada — só formata data, unidade (fração → %, BRL) e
 * traduz rótulo de enum. Política é global por produto (P-11): a mesma resposta para qualquer tenant.
 */

/** §2.3 — derivado no backend, nunca gravado. `INCOMPLETE` é o INACTIVE do PDF (Q-PP6-7). */
export type StatusPoliticaAgregada = 'ACTIVE' | 'INCOMPLETE' | 'RETIRED'

/** Status de uma VERSÃO de componente (§2.2, `ck_pcp_status`); `LEGADO` = ativa anterior à dupla aprovação. */
export type StatusVersaoPolitica = 'PENDENTE_APROVACAO' | 'ATIVA' | 'REJEITADA' | 'ENCERRADA' | 'LEGADO'

/** Área dona (`AreaDona` do common, §3.3). */
export type AreaDona = 'COMERCIAL' | 'FINANCEIRO' | 'OPERACOES' | 'JURIDICO'

export type MotivoAusencia = 'SEM_POLITICA' | 'NAO_APLICAVEL'

/**
 * `PoliticaAgregadaResponse.Componente` — uma versão de política. `propostoPor`/`aprovadoPor` (UUID, o `sub`) só
 * para o back-office: para o consultor o backend os omite e a tela, por garantia, também não os mostra.
 * Vigência SEMIABERTA `[vigenciaInicio, vigenciaFim)`: `vigenciaFim` é o primeiro dia em que já não vale.
 * `resumo` tem forma própria por componente (`PoliticaPublicadaEvento.Resumos`; elegibilidade: `{tipo, parametros,
 * hashRegra}`); a tela só muda a unidade dos números conhecidos.
 */
export interface ComponentePolitica {
  /** `ELEGIBILIDADE` | `FORMULARIO` | `CHECKLIST` | `SIMULACAO_PRODUTO` | `COMISSAO` | `TRIBUTOS` | … */
  componente?: string | null
  politicaId?: string | null
  /** Etapa do formulário, nome do tributo; nulo nos demais. */
  discriminador?: string | null
  versao?: string | null
  status?: StatusVersaoPolitica | string | null
  vigenciaInicio?: string | null
  vigenciaFim?: string | null
  origem?: string | null
  owner?: AreaDona | string | null
  propostoPor?: string | null
  aprovadoPor?: string | null
  aprovadoEm?: string | null
  resumo?: unknown
  /** `originei-simulacao`, `originei-oportunidades`, `originei-produtos`… */
  moduloOrigem?: string | null
  /** Rota de leitura no dono (hoje só a elegibilidade). */
  link?: string | null
}

/** `ComissaoProduto` — o que o modelo do produto diz da comissão (P5). */
export interface ComissaoDoProduto {
  modeloComissao?: string | null
  recorrencia?: string | null
  eventoAtivacao?: string | null
}

/** `Comissao` = política global (P4: faixas + percentual da receita líquida 360) + o que o produto diz (P5). */
export interface ComissaoAgregada {
  global?: ComponentePolitica | null
  produto?: ComissaoDoProduto | null
}

/** `Tributos` — retenções da simulação, do consultor e INDICATIVAS (Q73); uma versão por tributo. */
export interface TributosAgregados {
  /** Hoje `RETENCAO_CONSULTOR_INDICATIVA`. */
  natureza?: string | null
  itens?: ComponentePolitica[] | null
}

/** `Componentes` — ausente = omitido (e listado em `ausentes`). */
export interface ComponentesAgregados {
  elegibilidade?: ComponentePolitica | null
  formularios?: ComponentePolitica[] | null
  checklist?: ComponentePolitica | null
  simulacao?: ComponentePolitica | null
  comissao?: ComissaoAgregada | null
  tributos?: TributosAgregados | null
}

/** `Ausente` — `componente` usa as chaves de `componentes` (`elegibilidade`, `formularios`, …). */
export interface AusenciaComponente {
  componente: string
  motivo: MotivoAusencia | string
}

/**
 * `PoliticaAgregadaResponse.Pendente` — item da fila `GET /v1/produtos/politicas/pendentes` e de
 * `pendentesAprovacao` do agregado (só back-office). `linkAprovar`/`linkRejeitar` são a ação no módulo dono
 * (`/api/v1/…`); nulos quando o dono não tem rota conhecida. Sem `versaoRegistro`: a aprovação vai sem ela (o
 * backend a trata como opcional).
 */
export interface PoliticaPendente {
  componente: string
  politicaId: string
  /** Nulo = componente global (comissão, tributos, NF). */
  produtoId?: string | null
  produtoCodigo?: string | null
  discriminador?: string | null
  versao?: string | null
  vigenciaInicio?: string | null
  owner?: AreaDona | string | null
  origem?: string | null
  /** `sub` de quem propôs — comparado ao usuário logado para desabilitar Aprovar/Rejeitar. */
  propostoPor?: string | null
  propostoEm?: string | null
  resumo?: unknown
  moduloOrigem?: string | null
  linkAprovar?: string | null
  linkRejeitar?: string | null
  /** Revisão PP6-valr (8b): o PRÓPRIO proponente retira a proposta (PENDENTE → REJEITADA, sem segunda pessoa). */
  linkRetirar?: string | null
  /**
   * Revisão PP6-valr (A1): hash do conteúdo proposto (formulário `schemaHash`, checklist `conteudoHash`,
   * elegibilidade `hashRegra`). A aprovação devolve-o em `conteudoHash` — aprova-se o que foi revisado.
   */
  hashConteudo?: string | null
}

/** `GET /v1/produtos/{codigo}/politica?vigenteEm=` (§2.1). */
export interface PoliticaVigenteProduto {
  produtoCodigo: string
  produtoId?: string | null
  nome?: string | null
  statusCatalogo?: string | null
  statusPolitica: StatusPoliticaAgregada | string
  vigenteEm: string
  componentes: ComponentesAgregados
  ausentes?: AusenciaComponente[] | null
  /** Só back-office (o backend omite para o consultor). */
  pendentesAprovacao?: PoliticaPendente[] | null
  /** Último evento aplicado na projeção, por módulo dono (ISO instant). */
  sincronizacao?: Record<string, string | null> | null
}

export interface RejeitarPoliticaRequest {
  motivo: string
  versaoRegistro?: number
}

export interface AprovarPoliticaRequest {
  versaoRegistro?: number
  /** Revisão PP6-valr (A1): o hash do conteúdo revisado (formulário/checklist exigem; os demais ignoram). */
  conteudoHash?: string | null
}

/** Rótulos de exibição (tradução de enum — nada derivado). */
export const ROTULO_STATUS_POLITICA: Record<string, string> = {
  ACTIVE: 'Ativa',
  INCOMPLETE: 'Incompleta',
  RETIRED: 'Retirada',
}

export const ROTULO_STATUS_VERSAO: Record<string, string> = {
  PENDENTE_APROVACAO: 'Pendente de aprovação',
  ATIVA: 'Ativa',
  REJEITADA: 'Rejeitada',
  ENCERRADA: 'Encerrada',
  LEGADO: 'Legado — anterior à dupla aprovação',
}

export const ROTULO_MOTIVO_AUSENCIA: Record<string, string> = {
  SEM_POLITICA: 'Sem política vigente nesta data',
  NAO_APLICAVEL: 'Não se aplica a este produto',
}

export const ROTULO_OWNER: Record<string, string> = {
  COMERCIAL: 'Comercial',
  FINANCEIRO: 'Financeiro',
  OPERACOES: 'Operações',
  JURIDICO: 'Jurídico',
}

export const ROTULO_NATUREZA_TRIBUTOS: Record<string, string> = {
  RETENCAO_CONSULTOR_INDICATIVA: 'Retenção do consultor — indicativa (a efetiva é a da NF)',
  RETENCAO_CONSULTOR: 'Retenção do consultor',
}

/** Módulos de `sincronizacao` (o backend tira o prefixo `originei-`). */
export const ROTULO_MODULO: Record<string, string> = {
  simulacao: 'Simulação',
  oportunidades: 'Oportunidades',
  comissoes: 'Comissões',
  produtos: 'Produtos',
}

/** Nome do componente: chaves do agregado (minúsculas) e da fila (`componente` da projeção, maiúsculas). */
export const ROTULO_COMPONENTE: Record<string, string> = {
  elegibilidade: 'Elegibilidade',
  formularios: 'Formulários',
  checklist: 'Checklist documental',
  simulacao: 'Simulação',
  comissao: 'Comissão',
  tributos: 'Tributos (retenções indicativas)',
  ELEGIBILIDADE: 'Elegibilidade',
  FORMULARIO: 'Formulário',
  CHECKLIST: 'Checklist documental',
  SIMULACAO_PRODUTO: 'Simulação do produto',
  COMISSAO: 'Comissão',
  SPREAD: 'Spread',
  TRIBUTOS: 'Tributos',
  RETENCAO_NF: 'Retenção da NF',
  POLITICA_NF: 'Política da NF',
}

/** Ordem de exibição dos cartões. */
export const ORDEM_COMPONENTES = ['elegibilidade', 'formularios', 'checklist', 'simulacao', 'comissao', 'tributos'] as const

export const rotulo = (mapa: Record<string, string>, chave: string | null | undefined): string =>
  chave ? mapa[chave] ?? chave : '—'
